import { useRef, useState, useEffect } from "react";
import { BrowserQRCodeReader } from "@zxing/browser";

export default function QRScanner({ onScan }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const guideRef = useRef(null);
  const [detected, setDetected] = useState(false);

  useEffect(() => {
    const reader = new BrowserQRCodeReader();

    let frameId;
    let scanTimeoutId;
    let lastDecodeTime = 0;
    let hasScanned = false;
    let disposed = false;

    const drawGuideCrop = () => {
      const video = videoRef.current;
      const guide = guideRef.current;
      const canvas = canvasRef.current;

      if (
        !video ||
        !guide ||
        !canvas ||
        !video.videoWidth ||
        !video.videoHeight
      ) {
        frameId = requestAnimationFrame(drawGuideCrop);
        return;
      }

      const context = canvas.getContext("2d");

      const videoRect = video.getBoundingClientRect();
      const guideRect = guide.getBoundingClientRect();

      const scale = Math.max(
        videoRect.width / video.videoWidth,
        videoRect.height / video.videoHeight,
      );

      const renderedWidth = video.videoWidth * scale;
      const renderedHeight = video.videoHeight * scale;

      const croppedX = (renderedWidth - videoRect.width) / 2;
      const croppedY = (renderedHeight - videoRect.height) / 2;

      const sourceX = (guideRect.left - videoRect.left + croppedX) / scale;

      const sourceY = (guideRect.top - videoRect.top + croppedY) / scale;

      const sourceWidth = guideRect.width / scale;
      const sourceHeight = guideRect.height / scale;

      canvas.width = Math.round(sourceWidth);
      canvas.height = Math.round(sourceHeight);

      context.drawImage(
        video,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height,
      );

      const now = Date.now();

      if (!hasScanned && now - lastDecodeTime >= 250) {
        lastDecodeTime = now;

        try {
          const result = reader.decodeFromCanvas(canvas);
          const qrValue = result.getText();

          hasScanned = true;
          setDetected(true);

          scanTimeoutId = window.setTimeout(() => {
            if (!disposed) {
              onScan(qrValue);
            }
          }, 500);
        } catch {
          // No QR code found in this frame.
        }
      }

      frameId = requestAnimationFrame(drawGuideCrop);
    };

    const loadCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
          },
        });

        streamRef.current = stream;
        videoRef.current.srcObject = stream;

        await videoRef.current.play();

        drawGuideCrop();
      } catch (error) {
        console.error("Could not load camera:", error);
      }
    };

    loadCamera();

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      window.clearTimeout(scanTimeoutId);

      streamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, [onScan]);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-black">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div className="pointer-events-none absolute inset-0 grid place-items-center">
        <div
          ref={guideRef}
          className={`relative h-64 w-64 transition-transform duration-700 ${detected ? "scale-105" : "scale-100"}`}
        >
          <span
            className={`absolute left-0 top-0 h-16 w-16 rounded-tl border-l-8 border-t-8 transition-colors duration-500 ease-out ${detected ? "border-green-400" : "border-white"}`}
          />
          <span
            className={`absolute right-0 top-0 h-16 w-16 rounded-tr border-r-8 border-t-8 transition-colors duration-500 ease-out ${detected ? "border-green-400" : "border-white"}`}
          />
          <span
            className={`absolute bottom-0 left-0 h-16 w-16 rounded-bl border-b-8 border-l-8 transition-colors duration-500 ease-out ${detected ? "border-green-400" : "border-white"}`}
          />
          <span
            className={`absolute bottom-0 right-0 h-16 w-16 rounded-br border-b-8 border-r-8 transition-colors duration-500 ease-out ${detected ? "border-green-400" : "border-white"}`}
          />
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
